/**
 * @file shop-slug.ts
 * @description Slug магазина и его поддомен — phase19.28 этап А
 * (docs/business/platform.md PL5: латиница, цифры и дефис, есть список
 * зарезервированных, не меняется; db-design-domains-a §1 `Shop_slug_dns_check`
 * и §2 «Создание магазина»: поддомен `<slug>.<PLATFORM_BASE_DOMAIN>`).
 *
 * Зачем в shared: slug проверяют форма создания магазина в кабинете
 * платформы (подсказка сразу при вводе) и API (окончательно, 422/409), а
 * поддомен строят API при создании и кабинет в предпросмотре адреса.
 */

import { stripHostDecorations } from "./host-normalize.js";

/**
 * Slug — одна DNS-метка 3–40 символов. ДОЛЖНА СОВПАДАТЬ ДОСЛОВНО с
 * SQL-ограничением `Shop_slug_dns_check` (db-design-domains-a §1):
 *   CHECK (slug ~ '^[a-z0-9][a-z0-9-]{1,38}[a-z0-9]$') NOT VALID
 * Ровно 3–40 знаков, дефис не по краям (уточнено 2026-10-24: прежний вариант
 * с необязательной группой пропускал slug из одного символа).
 */
export const SHOP_SLUG_PATTERN = /^[a-z0-9][a-z0-9-]{1,38}[a-z0-9]$/;

/** Пределы длины slug для новых магазинов. */
export const SHOP_SLUG_MIN_LENGTH = 3;
export const SHOP_SLUG_MAX_LENGTH = 40;

/**
 * Зарезервированные slug: служебные поддомены платформы (кабинет, API,
 * статика, почта, мониторинг), слова, которые путаются с платформой, и
 * адреса почтовых ролей (`postmaster@`, `abuse@`) — их нельзя отдать
 * магазину, иначе поддомен перекроет служебный адрес или введёт в заблуждение.
 */
export const RESERVED_SHOP_SLUG_LIST: readonly string[] = Object.freeze([
  "www", "app", "api", "admin", "static", "edge", "mail", "cdn", "status", "help",
  "support", "docs", "blog", "shop", "store", "platform", "commora", "m", "dev", "test",
  "staging", "preview", "assets", "img", "media", "files", "auth", "login", "oauth", "id",
  "account", "lk", "pay", "billing", "noreply", "no-reply", "postmaster", "abuse", "dmarc", "hello",
  "privacy", "alerts", "ns1", "ns2", "mx", "smtp", "imap", "pop", "ftp", "git",
  "ci", "grafana", "minio", "s3",
]);

/** То же множеством — для быстрой проверки. */
export const RESERVED_SHOP_SLUGS: ReadonlySet<string> = new Set(RESERVED_SHOP_SLUG_LIST);

/** Почему slug не годится. Занятость (`shop_slug_taken`, 409) проверяет сервер по базе. */
export type ShopSlugError = "slug_invalid" | "slug_reserved";

/** Подсказки по-русски — текст ошибки API и поля формы. */
export const SHOP_SLUG_ERROR_MESSAGES: Readonly<Record<ShopSlugError, string>> = Object.freeze({
  slug_invalid:
    "Адрес магазина — 3–40 знаков: латиница в нижнем регистре, цифры и дефис (не в начале и не в конце).",
  slug_reserved: "Этот адрес занят платформой, выберите другой.",
});

export type ShopSlugResult = { data: string; error: null } | { data: null; error: ShopSlugError };

/** Подходит ли slug для нового магазина (формат и длина, затем резерв). */
export function validateShopSlug(slug: string): ShopSlugResult {
  if (slug.length < SHOP_SLUG_MIN_LENGTH || slug.length > SHOP_SLUG_MAX_LENGTH || !SHOP_SLUG_PATTERN.test(slug)) {
    return { data: null, error: "slug_invalid" };
  }
  if (RESERVED_SHOP_SLUGS.has(slug)) return { data: null, error: "slug_reserved" };
  return { data: slug, error: null };
}

/**
 * Поддомен магазина: `<slug>.<base>`. Резерв не проверяем (адрес строится и
 * для уже существующих магазинов), формат — проверяем: slug не по правилу
 * DNS дал бы адрес, который отвергнет `ShopDomain_host_check` (fail fast).
 */
export function buildSubdomainHost(slug: string, platformBaseDomain: string): string {
  if (!SHOP_SLUG_PATTERN.test(slug)) throw new RangeError(`shop_slug_invalid: ${slug}`);
  const base = stripHostDecorations(platformBaseDomain);
  if (base === "" || !base.includes(".")) throw new RangeError(`platform_base_domain_invalid: ${platformBaseDomain}`);
  return `${slug}.${base}`;
}
